import { resourceActionTypes, userActionTypes } from '../actions/types';
import { resourceService, userService } from '../services';
import _ from 'lodash';

const resetState = () => {
  return dispatch => dispatch({ type: resourceActionTypes.RESET_STATE });
};

const upload = (files, query = {}) => {
  return (dispatch, getState) => {
    const queue = {
      fileIndex: Number.isInteger(query.fileIndex) ? query.fileIndex : 0,
      total: Number.isInteger(query.total) && query.total > 0 ? query.total : 1,
    };
    dispatch(request({ progress: 0 }, queue));

    const user = getState().authentication.user;
    const userId = user.userId || user._id;

    return resourceService
      // Operator resources belong to the shared public library. Uploading
      // through /users/:id/resources marks them private and stores them in a
      // user directory, which makes template thumbnails and lesson assets
      // point at the wrong location.
      .upload(null, files, query, progress => dispatch(request({ progress }, queue)))
      .then(
        resource => {
          const thumbnailText = resource.type === 'Video' ? 'frame' : 'thumbnail';
          const srcThumbnail = `${process.env.REACT_APP_FILE_SERVER
            }/resources/${resource.type.toLowerCase()}s/${resource.name}/${thumbnailText}.${resource.extension
            }`;
          const src = srcThumbnail.replace(thumbnailText, 'screen');
          const uploadedResource = {
            ...queue,
            src: srcThumbnail,
            width: resource.width,
            height: resource.height,
            type: resource.type,
            price: resource.price,
            invisible: resource.invisible,
            data: {
              id: resource._id,
              src,
              srcThumbnail,
              alias: resource.alias,
              name: resource.name,
              extension: resource.extension,
              width: resource.width,
              height: resource.height,
              tags: resource.tags,
              tagsFa: resource.tagsFa,
              education: resource.education,
              liked: _.get(resource, 'liked', []).indexOf(userId) !== -1,
              ...resource.data,
            },
          }
          dispatch(success(uploadedResource));
        },
        error => {
          // Finish the upload state even when authentication expired, and
          // preserve the original error for the form and queue summary.
          dispatch(failure(queue, error));
          const authFailure = _refreshTokenOnFailure(
            error,
            resourceActionTypes.UPLOAD_FAILURE,
            getState().authentication.pendingRefreshingToken,
          );
          if (authFailure.type !== resourceActionTypes.UPLOAD_FAILURE) dispatch(authFailure);
        },
      );
  };

  function request(progress, fileIndex) {
    return { type: resourceActionTypes.UPLOAD_REQUEST, progress, fileIndex };
  }
  function success(resource) {
    return { type: resourceActionTypes.UPLOAD_SUCCESS, resource };
  }
  function failure(resource, error) {
    return { type: resourceActionTypes.UPLOAD_FAILURE, resource, error };
  }
};

const search = (query, clear) => {
  return (dispatch, getState) => {
    dispatch(request(query.type, query.limit));

    const userId = getState().authentication.user.userId;
    let resourceType = query.type;

    query = _.omitBy(query, value => _.isNil(value) || (_.isString(value) && _.isEmpty(value)));

    const countQuery = {
      ..._.omit(query, ['skip', 'limit', 'perGroupLimit', 'grouped']),
      count: true,
    };

    Promise.all([
      resourceService.findAll(null, { ...query }),
      query.grouped ? Promise.resolve(null) : resourceService.findAll(null, countQuery),
    ])
      .then(
        ([resources, total]) => {
          if (_.has(resources, '[0].resources')) {
            resources = resources.map(resourceGroup => {
              resourceGroup.resources = resourceGroup.resources.map((resource, index) => {
                const thumbnailText = resourceType === 'Video' ? 'frame' : 'thumbnail';
                const srcThumbnail = `${process.env.REACT_APP_FILE_SERVER
                  }/resources/${resource.type.toLowerCase()}s/${resource.name}/${thumbnailText}.${resource.extension
                  }`;
                const src = srcThumbnail.replace(thumbnailText, 'screen');
                return {
                  src: srcThumbnail,
                  width: resource.width,
                  height: resource.height,
                  key: index.toString(),
                  type: resource.type,
                  price: resource.price,
                  invisible: resource.invisible,
                  data: {
                    id: resource._id,
                    src,
                    srcThumbnail,
                    width: resource.width,
                    height: resource.height,
                    tags: resource.tags,
                    tagsFa: resource.tagsFa,
                    education: resource.education,
                    liked: _.get(resource, 'liked', []).indexOf(userId) !== -1,
                    ...resource.data,
                  },
                };
              });
              return resourceGroup;
            });
          } else {
            resources = resources.map((resource, index) => {
              const thumbnailText = resourceType === 'Video' ? 'frame' : 'thumbnail';
              const srcThumbnail = `${process.env.REACT_APP_FILE_SERVER
                }/resources/${resource.type.toLowerCase()}s/${resource.name}/${thumbnailText}.${resource.extension
                }`;
              const src = srcThumbnail.replace(thumbnailText, 'screen');
              return {
                src: srcThumbnail,
                width: resource.width,
                height: resource.height,
                key: ((parseInt(query.skip, 10) || 0) + index).toString(),
                type: resource.type,
                price: resource.price,
                invisible: resource.invisible,
                data: {
                  id: resource._id,
                  src,
                  srcThumbnail,
                  alias: resource.alias,
                  name: resource.name,
                  extension: resource.extension,
                  width: resource.width,
                  height: resource.height,
                  tags: resource.tags,
                  tagsFa: resource.tagsFa,
                  education: resource.education,
                  liked: _.get(resource, 'liked', []).indexOf(userId) !== -1,
                  ...resource.data,
                },
              };
            });
          }
          dispatch(success(
            resourceType,
            resources,
            parseInt(query.skip, 10) || 0,
            parseInt(query.limit, 10) || resources.length,
            true,
            typeof total === 'number' ? total : resources.length,
          ));
        },
        error => {
          dispatch(
            _refreshTokenOnFailure(
              error,
              resourceActionTypes.SEARCH_FAILURE,
              getState().authentication.pendingRefreshingToken,
              { resourceType },
            ),
          );
        },
      );
  };

  function request(resourceType, resourceLimit) {
    return { type: resourceActionTypes.SEARCH_REQUEST, resourceType, resourceLimit };
  }
  function success(resourceType, resources, skip, limit, clear, total) {
    return {
      type: resourceActionTypes.SEARCH_SUCCESS,
      resourceType,
      resources,
      skip,
      limit,
      clear,
      total,
    };
  }
};

const getImageTags = () => {
  return (dispatch, getState) => {
    dispatch(request());

    const query = { type: 'Image', grouped: true, perGroupLimit: 1 };

    resourceService.findAll(null, { ...query }).then(
      resources => {
        const imageTags = [];
        resources.forEach((resourceGroup, index) => {
          resourceGroup.resources.forEach(resource => {
            const srcThumbnail = `${process.env.REACT_APP_FILE_SERVER
              }/resources/${resource.type.toLowerCase()}s/${resource.name}/thumbnail.${resource.extension
              }`;
            const src = srcThumbnail.replace('thumbnail', 'screen');
            imageTags.push({
              src: srcThumbnail,
              width: resource.width,
              height: resource.height,
              key: index.toString(),
              type: resource.type,
              data: {
                id: resource._id,
                src,
                srcThumbnail,
                width: resource.width,
                height: resource.height,
                tags: resource.tags,
              },
            });
          });
        });
        dispatch(success(imageTags));
      },
      error => {
        dispatch(
          _refreshTokenOnFailure(
            error,
            resourceActionTypes.IMAGE_TAGS_FAILURE,
            getState().authentication.pendingRefreshingToken,
          ),
        );
      },
    );
  };

  function request() {
    return { type: resourceActionTypes.IMAGE_TAGS_REQUEST };
  }
  function success(imageTags) {
    return {
      type: resourceActionTypes.IMAGE_TAGS_SUCCESS,
      imageTags,
    };
  }
};

const getUserResource = type => {
  return (dispatch, getState) => {
    dispatch(request());

    const user = getState().authentication.user;
    const userId = user.userId || user._id;

    let resourceType = type;
    let skip;
    let limit;

    switch (resourceType) {
      case 'Image':
        skip = getState().resource.userImageSkip;
        limit = getState().resource.userImageLimit;
        break;
      case 'Video':
        skip = getState().resource.userVideoSkip;
        limit = getState().resource.userVideoLimit;
        break;
    }

    if (skip === null) {
      skip = 0;
    } else {
      skip = skip + limit;
    }

    resourceService
      .findAll(userId, {
        type,
        skip,
        limit,
      })
      .then(
        resources => {
          dispatch(success(resourceType, resources, skip, limit));
        },
        error => {
          dispatch(
            _refreshTokenOnFailure(
              error,
              resourceActionTypes.USER_RESOURCE_FAILURE,
              getState().authentication.pendingRefreshingToken,
            ),
          );
        },
      );
  };

  function request() {
    return { type: resourceActionTypes.USER_RESOURCE_REQUEST };
  }
  function success(resourceType, resources, skip, limit) {
    return {
      type: resourceActionTypes.USER_RESOURCE_SUCCESS,
      resourceType,
      resources,
      skip,
      limit,
    };
  }
};

const deleteResource = (type, resourceId) => {
  return (dispatch, getState) => {
    dispatch(request());
        
    const user = getState().authentication.user;
    const userId = user.userId || user._id;

    resourceService._delete(userId, resourceId).then(
      () => {
        let resources;
        switch (type) {
          case 'UserImage':
            resources = [...getState().resource.userImages];
            _.remove(resources, resource => {
              return resource._id === resourceId;
            });
        }
        dispatch(success(type, resources));
      },
      error => {
        dispatch(
          _refreshTokenOnFailure(
            error,
            resourceActionTypes.DELETE_FAILURE,
            getState().authentication.pendingRefreshingToken,
          ),
        );
      },
    );
  };

  function request() {
    return { type: resourceActionTypes.DELETE_REQUEST };
  }
  function success(resourceType, resources) {
    return {
      type: resourceActionTypes.DELETE_SUCCESS,
      resourceType,
      resources,
    };
  }
};

const getUserFolders = () => {
  return (dispatch, getState) => {
    dispatch(request());

    const userId = getState().authentication.user.userId;

    userService.findAllFolders(userId).then(
      folders => {
        dispatch(success(folders));
      },
      error => {
        dispatch(
          _refreshTokenOnFailure(
            error,
            resourceActionTypes.USER_FOLDERS_FAILURE,
            getState().authentication.pendingRefreshingToken,
          ),
        );
      },
    );
  };

  function request() {
    return { type: resourceActionTypes.USER_FOLDERS_REQUEST };
  }
  function success(folders) {
    return {
      type: resourceActionTypes.USER_FOLDERS_SUCCESS,
      folders,
    };
  }
};

const getFolderResources = (folderId, clear) => {
  return (dispatch, getState) => {
    dispatch(request());

    const userId = getState().authentication.user.userId;

    let skip = getState().resource[`${folderId}Skip`];
    let limit = getState().resource[`${folderId}Limit`];
    if (!limit) {
      limit = 16;
    }

    if (clear || skip === null) {
      skip = 0;
    } else {
      skip = skip + limit;
    }

    resourceService.findAllInFolder(userId, folderId, { skip, limit }).then(
      result => {
        const offset = clear ? 0 : _.get(getState().resource, `${folderId}s`, []).length;
        let resources = _.get(result, '[0].folders.[0].resources', []);
        resources = resources.map((resource, index) => {
          const thumbnailText = resource.type === 'Video' ? 'frame' : 'thumbnail';
          const srcThumbnail = resource.private
            ? `${process.env.REACT_APP_FILE_SERVER}/users/${userId}/resources/${resource.name}/${thumbnailText}.${resource.extension}`
            : `${process.env.REACT_APP_FILE_SERVER}/resources/${resource.type.toLowerCase()}s/${resource.name
            }/${thumbnailText}.${resource.extension}`;
          const src = srcThumbnail.replace(thumbnailText, 'screen');
          return {
            src: srcThumbnail,
            width: resource.width,
            height: resource.height,
            key: (offset + index).toString(),
            type: resource.type,
            data: {
              id: resource._id,
              src,
              srcThumbnail,
              width: resource.width,
              height: resource.height,
              tags: resource.tags,
              liked: _.get(resource, 'liked', []).indexOf(userId) !== -1,
            },
          };
        });
        dispatch(success(folderId, resources, skip, limit, clear));
      },
      error => {
        dispatch(
          _refreshTokenOnFailure(
            error,
            resourceActionTypes.USER_FOLDER_RESOURCES_FAILURE,
            getState().authentication.pendingRefreshingToken,
          ),
        );
      },
    );
  };

  function request() {
    return { type: resourceActionTypes.USER_FOLDER_RESOURCES_REQUEST };
  }
  function success(folderId, resources, skip, limit, clear) {
    return {
      type: resourceActionTypes.USER_FOLDER_RESOURCES_SUCCESS,
      folderName: folderId,
      resources,
      skip,
      limit,
      clear,
    };
  }
};

const createFolder = folderName => {
  return (dispatch, getState) => {
    dispatch(request());

    const user = getState().authentication.user;
    const userId = user.userId || user._id;

    userService.createFolder(userId, folderName).then(
      folders => {
        dispatch(success(folders));
      },
      error => {
        dispatch(
          _refreshTokenOnFailure(
            error,
            resourceActionTypes.CREATE_FOLDER_FAILURE,
            getState().authentication.pendingRefreshingToken,
          ),
        );
      },
    );
  };

  function request() {
    return { type: resourceActionTypes.CREATE_FOLDER_REQUEST };
  }
  function success(folders) {
    return {
      type: resourceActionTypes.CREATE_FOLDER_SUCCESS,
      folders,
    };
  }
};

const deleteFolder = folderId => {
  return (dispatch, getState) => {
    dispatch(request());

    const user = getState().authentication.user;
    const userId = user.userId || user._id;

    userService.deleteFolder(userId, folderId).then(
      folders => {
        dispatch(success(folders));
      },
      error => {
        dispatch(
          _refreshTokenOnFailure(
            error,
            resourceActionTypes.DELETE_FOLDER_FAILURE,
            getState().authentication.pendingRefreshingToken,
          ),
        );
      },
    );
  };

  function request() {
    return { type: resourceActionTypes.DELETE_FOLDER_REQUEST };
  }
  function success(folders) {
    return {
      type: resourceActionTypes.DELETE_FOLDER_SUCCESS,
      folders,
    };
  }
};

const addToFolder = (folderId, resource) => {
  return (dispatch, getState) => {
    dispatch(request());

    const user = getState().authentication.user;
    const userId = user.userId || user._id;

    resourceService.userFolderAction(userId, resource.data.id, folderId, 'add').then(
      () => {
        dispatch(success(folderId, resource));
      },
      error => {
        dispatch(
          _refreshTokenOnFailure(
            error,
            resourceActionTypes.ADD_TO_FOLDER_FAILURE,
            getState().authentication.pendingRefreshingToken,
          ),
        );
      },
    );
  };

  function request() {
    return { type: resourceActionTypes.ADD_TO_FOLDER_REQUEST };
  }
  function success(folderId, resource) {
    return { type: resourceActionTypes.ADD_TO_FOLDER_SUCCESS, folderId, resource };
  }
};

const removeFromFolder = (folderId, resourceId, index) => {
  return (dispatch, getState) => {
    dispatch(request());

    const user = getState().authentication.user;
    const userId = user.userId || user._id;

    resourceService.userFolderAction(userId, resourceId, folderId, 'remove').then(
      () => {
        const folderResources = [...getState().resource[`${folderId.toLowerCase()}s`]];
        folderResources.splice(index, 1);
        dispatch(success(folderId, folderResources));
      },
      error => {
        dispatch(
          _refreshTokenOnFailure(
            error,
            resourceActionTypes.REMOVE_FROM_FOLDER_FAILURE,
            getState().authentication.pendingRefreshingToken,
          ),
        );
      },
    );
  };

  function request() {
    return { type: resourceActionTypes.REMOVE_FROM_FOLDER_REQUEST };
  }
  function success(folderId, resources) {
    return { type: resourceActionTypes.REMOVE_FROM_FOLDER_SUCCESS, folderId, resources };
  }
};

const userAction = (type, resourceId, action) => {
  return (dispatch, getState) => {
    dispatch(request());

    const userId = getState().authentication.user.userId;

    resourceService.userAction(userId, resourceId, action).then(
      () => {
        const resources = [...getState().resource[`${type.toLowerCase()}s`]];
        if (_.has(resources, '[0].resources')) {
          resources.map(resourceGroup => {
            resourceGroup.resources.map(resource => {
              if (resource.data.id === resourceId) {
                switch (action) {
                  case 'like':
                    resource.data.liked = true;
                    break;
                  case 'unlike':
                    resource.data.liked = false;
                    break;
                }
              }
            });
          });
        } else {
          resources.map(resource => {
            if (resource.data.id === resourceId) {
              switch (action) {
                case 'like':
                  resource.data.liked = true;
                  break;
                case 'unlike':
                  resource.data.liked = false;
                  break;
              }
            }
          });
        }
        dispatch(success(type, resources));
      },
      error => {
        dispatch(
          _refreshTokenOnFailure(
            error,
            resourceActionTypes.USER_ACTION_FAILURE,
            getState().authentication.pendingRefreshingToken,
          ),
        );
      },
    );
  };

  function request() {
    return { type: resourceActionTypes.USER_ACTION_REQUEST };
  }
  function success(resourceType, resources) {
    return {
      type: resourceActionTypes.USER_ACTION_SUCCESS,
      resourceType,
      resources,
    };
  }
};

const updateResource = (resourceId, params) => {
  return (dispatch, getState) => {
    dispatch(request());

    resourceService.update(resourceId, params).then(
      resource => {
        const thumbnailText = resource.type === 'Video' ? 'frame' : 'thumbnail';
        const srcThumbnail = `${process.env.REACT_APP_FILE_SERVER
          }/resources/${resource.type.toLowerCase()}s/${resource.name}/${thumbnailText}.${resource.extension
          }`;
        const src = srcThumbnail.replace(thumbnailText, 'screen');
        const updatedResource = {
          src: srcThumbnail,
          width: resource.width,
          height: resource.height,
          type: resource.type,
          price: resource.price,
          invisible: resource.invisible,
          data: {
            id: resource._id,
            src,
            srcThumbnail,
            alias: resource.alias,
            name: resource.name,
            extension: resource.extension,
            width: resource.width,
            height: resource.height,
            tags: resource.tags,
            tagsFa: resource.tagsFa,
            education: resource.education,
            ...resource.data,
          },
        };
        dispatch(success(updatedResource));
      },
      error => {
        dispatch(
          _refreshTokenOnFailure(
            error,
            resourceActionTypes.UPDATE_FAILURE,
            getState().authentication.pendingRefreshingToken,
          ),
        );
      },
    );
  };

  function request() {
    return { type: resourceActionTypes.UPDATE_REQUEST };
  }
  function success(resource) {
    return {
      type: resourceActionTypes.UPDATE_SUCCESS,
      resource,
    };
  }
};

const _refreshTokenOnFailure = (error, failureAction, pendingRefreshToken, restParams = {}) => {
  if (error.error === 'jwt expired') {
    if (pendingRefreshToken) {
      return {
        type: userActionTypes.PENDING_REFRESH_TOKEN,
        error,
      };
    } else {
      return { type: userActionTypes.INVALID_TOKEN, error };
    }
  } else {
    return { type: failureAction, error, ...restParams };
  }
};

export const resourceActions = {
  resetState,
  upload,
  search,
  getUserResource,
  deleteResource,
  userAction,
  getUserFolders,
  getFolderResources,
  createFolder,
  deleteFolder,
  addToFolder,
  removeFromFolder,
  getImageTags,
  updateResource,
};
